/**
 * Mobile Add to Cart: sticky until it reaches its natural position.
 *
 * The button is pinned to the bottom of the viewport while its real place in
 * the page is still below the fold. Once you scroll far enough that its
 * natural position comes into view, it settles there.
 *
 * The button is wrapped in a shell that always reserves its exact height, and
 * the button is positioned inside that shell - absolute when settled, fixed
 * when pinned. Because the shell never changes size, switching between the two
 * causes no layout shift at all. That matters more than it sounds: the earlier
 * version collapsed the shell on release, which moved the very boundary the
 * observer was watching, so on mobile Safari - where the collapsing URL bar
 * resizes the viewport mid-scroll - it flipped back and forth and looked jumpy.
 *
 * Mobile only; desktop keeps the button in normal flow.
 */
(function () {
  var MOBILE = '(max-width: 768px)';

  function init() {
    var button = document.querySelector('.template-product button[data-add-to-cart]');
    if (!button || button.dataset.atcStickyBound) return;
    button.dataset.atcStickyBound = 'true';

    var shell = document.createElement('div');
    shell.className = 'atc-sticky-shell';
    button.parentNode.insertBefore(shell, button);
    shell.appendChild(button);

    var mq = window.matchMedia(MOBILE);
    var observer = null;
    var stuck = null;

    // Preorder apps can insert their delivery message with `afterend` after
    // this script has wrapped the button. In that case the message lands
    // inside the fixed-height shell and overlaps the button. Keep the shell
    // button-only and move any injected siblings back into the form flow.
    function releaseInjectedSiblings() {
      if (!shell.parentNode) return;

      var anchor = shell.nextSibling;
      Array.prototype.slice.call(shell.children).forEach(function (child) {
        if (child !== button) {
          shell.parentNode.insertBefore(child, anchor);
        }
      });
    }

    var shellObserver = new MutationObserver(releaseInjectedSiblings);
    shellObserver.observe(shell, { childList: true });
    releaseInjectedSiblings();

    function measure() {
      // Measure against the shell, never the button - once the button is
      // positioned its own box no longer reflects the space it should occupy.
      var h = button.getBoundingClientRect().height;
      if (h) {
        shell.style.height = h + 'px';
        document.documentElement.style.setProperty('--atc-sticky-height', h + 'px');
      }
    }

    function setStuck(next) {
      if (stuck === next) return;
      stuck = next;
      button.classList.toggle('is-stuck', next);
      document.documentElement.classList.toggle('atc-sticky-active', next);
      document.documentElement.classList.toggle('atc-sticky-active', next);
    }

    function enable() {
      measure();
      // Decide synchronously on first paint. If the shell is already in the
      // viewport, leave the button in flow so STOQ's date below it stays visible.
      var initialRect = shell.getBoundingClientRect();
      setStuck(initialRect.top > window.innerHeight);

      // Shoe PDPs place the custom size selector immediately before the shell.
      // Waiting for the shell itself to appear lets that control slide under
      // the fixed CTA on iPhone Safari. Release one CTA-height early on those
      // pages; products without a size selector keep the original late-release
      // behavior. The extra 26px covers the 10px bottom inset, 10px white
      // surround, and a small visual gap.
      var sizeSelector = document.querySelector('.template-product #size-display');
      var releaseMargin = '-24px';
      if (sizeSelector && (sizeSelector.offsetWidth || sizeSelector.offsetHeight)) {
        releaseMargin = Math.ceil(button.getBoundingClientRect().height + 26) + 'px';
      }

      observer = new IntersectionObserver(
        function (entries) {
          var e = entries[0];
          if (!e) return;
          // Pin only while the shell's place in the page is still below the
          // viewport. Once any of it is on screen, or we've scrolled past it,
          // the button belongs in the page.
          setStuck(!e.isIntersecting && e.boundingClientRect.top > 0);
        },
        // Extending the observation area below the viewport keeps preceding
        // size/color controls clear of the fixed button while still basing the
        // state on the stable shell (not Safari's resizing URL bar).
        { threshold: 0, rootMargin: '0px 0px ' + releaseMargin + ' 0px' }
      );

      observer.observe(shell);
    }

    function disable() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      setStuck(false);
      shell.style.height = '';
      document.documentElement.style.removeProperty('--atc-sticky-height');
      document.documentElement.style.removeProperty('--atc-sticky-height');
    }

    function apply() {
      if (mq.matches) {
        if (!observer) enable();
      } else {
        disable();
      }
    }

    apply();

    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
    } else if (mq.addListener) {
      mq.addListener(apply);
    }

    // Re-measure when the button's label changes (Sold Out / Coming Soon) or
    // the page reflows. Deliberately does NOT touch the stuck state - mobile
    // browsers fire resize as the URL bar collapses, and re-deciding there is
    // what made it jump.
    var pending = null;
    window.addEventListener(
      'resize',
      function () {
        if (pending) cancelAnimationFrame(pending);
        pending = requestAnimationFrame(function () {
          if (mq.matches) measure();
        });
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Product content can be swapped in by the theme (quick view, variant
  // re-render), so re-bind when the theme signals its assets are ready.
  document.addEventListener('page:loaded', init);
})();
